import React from 'react';

import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useRouteError,
} from 'react-router';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { useAppSelector } from './reducers/hooks';

import { ParallaxProvider, config } from '@adc/parallax-component-library';

import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

import LoginForm from './components/auth/LoginForm';
import ForgotPassword from './components/auth/ForgotPassword';

import Dashboard from './components/pages/Dashboard';
import Organizations from './components/pages/Organizations';
import Studies from './components/pages/Studies';
import Study from './components/pages/Study';
import StudyCreate from './components/pages/StudyCreate';
import Site from './components/pages/Site';
import Subject from './components/pages/Subject';
import Sensor from './components/pages/Sensor';
import UnexpectedError from './components/utility/UnexpectedError';
import NotFound from './components/utility/NotFound';
import Accounts from './components/pages/Accounts';
import RoleAssignments from './components/pages/RoleAssignments';
import { Account } from './components/pages/Account';

const queryClient = new QueryClient();

/**
 * Prevent the user from accessing protected routes if they are not logged in.
 * @returns {React.JSX.Element}
 */
function ProtectedRoute() {
  const isLoggedIn = useAppSelector((state) => state.auth.loggedIn);

  if (!isLoggedIn) {
    return <Navigate to="auth/login" replace />;
  }

  return <Outlet />;
}

/**
 * Prevent the user from accessing auth routes if they are already logged in.
 * @returns {React.JSX.Element}
 */
function AuthRoute() {
  const isLoggedIn = useAppSelector((state) => state.auth.loggedIn);

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

function RouterErrorMessage() {
  const routeError = useRouteError();

  return <UnexpectedError errorMessage={String(routeError)} />;
}

const routes = [
  {
    path: '/auth',
    element: <AuthLayout />,
    errorElement: <RouterErrorMessage />,
    children: [
      {
        element: <AuthRoute />,
        children: [
          { index: true, element: <Navigate to="login" replace /> },
          { path: 'login', element: <LoginForm /> },
          { path: 'forgot-password', element: <ForgotPassword /> },
        ],
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    errorElement: <RouterErrorMessage />,
    children: [
      {
        element: <MainLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard" replace />,
          },
          { path: 'dashboard', element: <Dashboard /> },
          { path: 'organizations', element: <Organizations /> },
          {
            path: 'accounts',
            children: [
              { index: true, element: <Accounts /> },
              { path: ':accountKey', element: <Account /> },
            ],
          },
          {
            path: 'role-assignments',
            children: [{ index: true, element: <RoleAssignments /> }],
          },
          {
            path: 'studies',
            children: [
              { index: true, element: <Studies /> },
              { path: 'create', element: <StudyCreate /> },
              { path: ':studyKey', element: <Study /> },
            ],
          },
          {
            path: 'sites',
            children: [
              { index: true, element: <Navigate to="/" replace /> },
              { path: ':siteKey', element: <Site /> },
            ],
          },
          {
            path: 'subjects',
            children: [
              { index: true, element: <Navigate to="/" replace /> },
              { path: ':subjectKey', element: <Subject /> },
            ],
          },
          {
            path: 'sensors',
            children: [
              { index: true, element: <Navigate to="/" replace /> },
              { path: ':sensorKey', element: <Sensor /> },
            ],
          },
          {
            path: '*',
            element: <NotFound />,
          },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
];

export const router = createBrowserRouter(routes);

/**
 * The main application component that sets up the routing and layout
 * @returns {React.JSX.Element}
 */
export function App() {
  return (
    <ParallaxProvider
      config={config}
      locale="en-US"
      dir="ltr"
      defaultTheme="light"
    >
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ReactQueryDevtools />
      </QueryClientProvider>
    </ParallaxProvider>
  );
}

export default App;
